import React from 'react';
import { useThemeUI } from 'theme-ui';

interface ImageTransformOptions {
  width?: number;
  height?: number;
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  quality?: number;
  format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png';
  dpr?: number;
}

//TODO : make image transform utility
const createImageUrl = (url: string, options: ImageTransformOptions) => {
  if (
    typeof window !== 'undefined' &&
    (window.location.href.indexOf('localhost') > -1 ||
      window.location.href.indexOf('rockefellercenter.netlify.app') > -1)
  ) {
    return url;
  }

  const params = [];
  if (options.width) params.push(`w=${options.width.toString()}`);
  if (options.height) params.push(`h=${options.height.toString()}`);
  if (options.fit) params.push(`fit=${options.fit}`);
  if (options.quality) params.push(`q=${options.quality.toString()}`);
  if (options.format) params.push(`f=${options.format}`);

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  if (options.dpr || dpr)
    params.push(`dpr=${options.dpr ? options.dpr.toString() : dpr.toString()}`);

  // Use the Cloudflare Image CDN endpoint
  return `https://rockefellercenter.com/cdn-cgi/image/${params.join(
    ',',
  )}${url}`;
};

interface PictureInterface {
  src: string;
  alt: string;
  imageSizes?: number[];
  imageStyle?: React.CSSProperties;
  objectFit?: React.CSSProperties['objectFit'];
  objectPosition?: React.CSSProperties['objectPosition'];
  maxWidth?: React.CSSProperties['maxWidth'];
  ratio?: number;
}

const Picture = ({
  src,
  alt,
  imageSizes,
  imageStyle,
  objectFit = 'cover',
  objectPosition = 'center',
  maxWidth,
  ratio,
  ...props
}: PictureInterface) => {
  const { theme } = useThemeUI();
  const themeBreakpoints = theme.breakpoints as string[];
  const defaultImageSizes = themeBreakpoints
    .map((bp) => parseInt(bp, 10))
    .sort((a, b) => a - b);
  const sizes = imageSizes || defaultImageSizes;

  let imagesSrc: string[] = [];

  if (src.indexOf('http') === 0) {
    const url = new URL(src);
    if (url.hostname.indexOf('rockefellercenter.com') > -1) {
      sizes.forEach((size) => {
        imagesSrc.push(createImageUrl(url.pathname, { width: size }));
      });
    } else {
      imagesSrc = [src];
    }
  } else {
    sizes.forEach((size) => {
      imagesSrc.push(createImageUrl(src, { width: size }));
    });
  }

  const imgStyle: React.CSSProperties = {
    ...imageStyle,
    width: '100%',
    height: '100%',
    objectFit,
    objectPosition,
    maxWidth,
    aspectRatio: ratio,
  };

  return (
    <picture {...props}>
      {imagesSrc.length === 1 ? (
        <source srcSet={imagesSrc[0]} />
      ) : (
        imagesSrc.map((src, index) => (
          <source
            key={index}
            media={`(min-width: ${sizes[index]}px)`}
            srcSet={src}
          />
        ))
      )}
      <img alt={alt} src={`${src}`} style={imgStyle} />
    </picture>
  );
};

export default Picture;
